import { useState } from 'react';
import { fill, type PricingCopy } from './pricingCopy';
import {
  DETAIL_ROWS,
  MAIN_INPUTS,
  NEED_IDS,
  PRESETS,
  TIER_ORDER,
  fmt,
  whyVars,
  type CalcState,
  type DetailKey,
  type MainInputKey,
  type NeedId,
  type PathId,
  type PresetId,
  type Quote,
  type Tier,
  type TierId,
} from './pricingModel';
import { Btn, C, Check, EASE, MONO, SealRow, Section, mono, unlockRows, type Mode } from './ui';

/*
 * El bloque que xotski.com enmarca: las pestañas «The plans / Size my year»,
 * las cuatro tarjetas y la calculadora, con los textos y las reglas del build
 * del 29 sep 2026. Es el mismo componente en la página de precios de la app y
 * en `/embed/pricing`; lo que cambia es qué pasa al pulsar un botón (`onGo`):
 * la página abre su checkout, el marco pide al sitio que navegue.
 *
 * El estado de la calculadora lo lleva quien lo monta, porque la página lo
 * necesita también para el resumen del pedido y el formulario de contacto.
 */

/** Por qué la configuración exige un nivel mínimo, ya con su `{n}`. */
export const whyTextOf = (c: PricingCopy, q: Quote, T: Record<TierId, Tier>) =>
  q.why ? fill(c.calc.why[q.why], whyVars(q.why, T)) : null;
/** El margen de tolerancia del camino (à la carte lleva el de Free). */
export const graceOf = (T: Record<TierId, Tier>, id: PathId | null) => (id && id !== 'alc' ? T[id] : T.free).grace;
/** El total que encabeza la respuesta: «Talk to us» cuando no hay precio cerrado. */
export const recTotalOf = (c: PricingCopy, q: Quote) => {
  const rec = q.paths[q.rec];
  return rec.total == null ? c.calc.answer.talkToUs : q.rec === 'enterprise' ? fmt(rec.total) + '+' : fmt(rec.total);
};
/** La llamada a la acción de la respuesta. */
export const recCtaOf = (c: PricingCopy, q: Quote) =>
  q.rec === 'free' ? c.calc.answer.startFree
    : q.rec === 'enterprise' ? c.calc.answer.talkToUs
      : q.rec === 'alc' ? c.calc.answer.startSeat
        : fill(c.calc.answer.choose, { name: q.paths[q.rec].name });

export interface PlansBlockProps {
  c: PricingCopy;
  T: Record<TierId, Tier>;
  q: Quote;
  calc: CalcState;
  preset: PresetId | null;
  mode: Mode;
  narrow: boolean;
  onMode: (m: Mode) => void;
  onGo: (tier: PathId) => void;
  onNum: (k: MainInputKey | DetailKey, v: string | number, min: number, max: number) => void;
  onStep: (k: MainInputKey, d: number, min: number, max: number) => void;
  onToggleNeed: (k: NeedId) => void;
  onPreset: (id: PresetId) => void;
  /** Dentro de un marco: sin los márgenes exteriores de la página. */
  embedded?: boolean;
}

export function PlansBlock({ c, T, q, calc, preset, mode, narrow, onMode, onGo, onNum, onStep, onToggleNeed, onPreset, embedded = false }: PlansBlockProps) {
  const rec = q.paths[q.rec];
  const [detail, setDetail] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const pad = embedded ? '12px 0 24px' : '20px 28px 72px';
  const go = onGo;
  const lineLabel = (id: keyof PricingCopy['calc']['lines']) => c.calc.lines[id];
  const setMode = onMode;
  const num = onNum;
  const step = onStep;
  const toggleNeed = onToggleNeed;
  const applyPreset = onPreset;

  // ── Derivados de la cotización (renderVals de la maqueta) ──
  const rungs = TIER_ORDER;
  const recRung: TierId = q.rec === 'alc' ? 'free' : q.rec;
  const ri = rungs.indexOf(recRung);
  const neighbours: Array<{ title: string; delta: string; deltaColor: string; body: string; pick: () => void }> = [];
  if (ri > 0) {
    const id = rungs[ri - 1]!;
    const p = q.paths[id];
    neighbours.push({
      title: fill(c.calc.answer.stepDown, { name: T[id].name }),
      delta: p.total == null ? c.calc.answer.na : '−' + fmt(Math.max(0, (rec.total ?? 0) - p.total)),
      deltaColor: C.deep, body: c.calc.neighbours[id], pick: () => go(id),
    });
  }
  if (ri < rungs.length - 1) {
    const id = rungs[ri + 1]!;
    const p = q.paths[id];
    neighbours.push({
      title: fill(c.calc.answer.stepUp, { name: T[id].name }),
      delta: p.total == null ? c.calc.answer.talkToUs : '+' + fmt(Math.max(0, p.total - (rec.total ?? 0))),
      deltaColor: C.accent, body: c.calc.neighbours[id], pick: () => go(id),
    });
  }

  const whyText = whyTextOf(c, q, T);
  let bannerText: string | null = null;
  let bannerBg: string = C.soft;
  let bannerDot: string = C.accent;
  const lime = () => { bannerBg = 'rgba(198,217,59,0.15)'; bannerDot = C.lime; };
  if (q.freeWall) { bannerText = c.calc.banners.wall; lime(); }
  else if (rec.unlimited) { bannerText = c.calc.banners.unlimited; lime(); }
  else if (q.rec === 'free') bannerText = c.calc.banners.free;
  else if (q.rec === 'enterprise') bannerText = fill(c.calc.banners.enterprise, { why: whyText ?? c.calc.banners.enterpriseDefault });
  else if (q.paths.alc.total != null && q.rec !== 'alc' && rec.total != null && q.paths.alc.total > rec.total) {
    bannerText = fill(c.calc.banners.alcMore, { amount: fmt(q.paths.alc.total - rec.total) });
    lime();
  } else if (q.rec === 'xk1' && q.paths.xk2.total != null && q.paths.xk1.total != null && q.paths.xk2.total <= q.paths.xk1.total * 1.2) {
    bannerText = fill(c.calc.banners.xk2Close, { amount: fmt(q.paths.xk2.total - q.paths.xk1.total) });
  }

  const activeNeeds = NEED_IDS.filter((n) => calc.needs[n]).map((n) => c.calc.needs[n]);
  const detailSummary = fill(c.calc.summaryLine, { t: calc.t, l: calc.l, pr: calc.pr, ed: calc.ed });

  const recCta = recCtaOf(c, q);
  const recGo = () => go(q.rec === 'alc' ? 'free' : q.rec);
  const recTotal = recTotalOf(c, q);

  return (
    <>
      <section style={{ padding: embedded ? '0 0 8px' : '0 28px 12px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 1140 }}>
          <div style={{ display: 'inline-flex', gap: 4, padding: 4, borderRadius: 999, border: `1px solid ${C.line}`, background: C.soft }}>
            {(['plans', 'calc'] as Mode[]).map((m) => (
              <Btn key={m} onClick={() => setMode(m)} style={{ padding: '11px 22px', borderRadius: 999, background: mode === m ? C.lime : 'transparent', color: mode === m ? C.ink : '#666666', boxShadow: mode === m ? '0 2px 8px rgba(27,15,43,0.18)' : 'none', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', transition: `all 150ms ${EASE}` }}>
                {c.modes[m]}
              </Btn>
            ))}
          </div>
        </div>
      </section>

      {/* ── Los planes ── */}
      {mode === 'plans' ? (
        <Section style={{ padding: pad }}>
          <div style={{ display: 'grid', gap: 22 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(252px, 1fr))', gap: 16, alignItems: 'stretch' }}>
              {TIER_ORDER.map((id) => {
                const base = T[id].base;
                const open = id === 'enterprise';
                const hero = id === 'xk2';
                const accents: Record<TierId, string> = { free: C.lilac, xk1: C.accent, xk2: C.lime, enterprise: C.ink };
                const badge = hero ? c.plans.mostPopular : '';
                const primary = hero;
                return (
                  <div key={id} style={{ background: hero ? '#FBFAFE' : C.white, border: hero ? `2px solid ${C.accent}` : `1.5px solid ${C.lilacLine}`, borderRadius: 20, overflow: 'hidden', height: '100%', boxSizing: 'border-box', display: 'grid', gridTemplateRows: '6px auto auto 1fr', boxShadow: hero ? '0 6px 20px rgba(27,15,43,0.12)' : 'none' }}>
                    <div style={{ background: accents[id] }} />
                    <div style={{ display: 'grid', gap: 10, alignContent: 'start', padding: '22px 24px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 24 }}>
                        <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 20, lineHeight: '24px', letterSpacing: '-0.02em', color: C.ink }}>{T[id].name}</div>
                        {badge ? (
                          <div style={{ fontSize: 11, lineHeight: '16px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '4px 9px', borderRadius: 999, background: hero ? C.lime : 'rgba(178,98,245,0.14)', color: hero ? C.ink : C.deep }}>{badge}</div>
                        ) : null}
                      </div>
                      <div style={mono({ fontSize: 34, lineHeight: '38px', letterSpacing: '-0.03em' })}>
                        {base === 0 ? '$0' : open ? c.plans.from + fmt(base) : fmt(base)}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: '19px', color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {base === 0 ? c.plans.freePer : fill(open ? c.plans.perFrom : c.plans.perMonth, { amount: fmt(base / 12) })}
                      </div>
                      <div style={{ fontSize: 14, lineHeight: '21px', color: C.mid, textWrap: 'pretty', minHeight: 63 }}>{c.plans.cards[id].blurb}</div>
                    </div>
                    <div style={{ padding: '0 24px' }}>
                      <Btn onClick={() => go(id)} hover="xkp-hover-border" style={{ height: 48, borderRadius: 999, background: primary ? C.accent : C.white, border: `1.5px solid ${primary ? C.accent : C.lilac}`, color: primary ? C.white : C.deep, fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `all 150ms ${EASE}` }}>
                        {c.plans.cards[id].cta}
                      </Btn>
                    </div>
                    <div style={{ display: 'grid', gap: 8, alignContent: 'start', borderTop: `1px solid ${C.hair}`, margin: '16px 24px 0', padding: '16px 0 16px' }}>
                      {unlockRows(c.unlocks[id], true, c.plans.soon).map((b, i) => <Check key={i} soon={b.soon}>{b.text}</Check>)}
                    </div>
                    {id === 'free' ? (
                      <div style={{ margin: '0 24px 16px', padding: '10px 12px', borderRadius: 10, background: 'rgba(198,217,59,0.18)', fontSize: 12, lineHeight: '18px', color: C.ink, textWrap: 'pretty' }}>
                        {c.plans.freeWall}
                      </div>
                    ) : null}
                    <div style={{ margin: 'auto 24px 0', padding: '14px 0 18px', borderTop: `1px solid ${C.hair}` }}>
                      <SealRow text={c.plans.seal} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: 14, lineHeight: '21px', color: C.mid }}>
              <span>{c.plans.notSure}</span>
              <Btn onClick={() => setMode('calc')} hover="xkp-hover-ink" style={{ fontWeight: 600, color: C.deep }}>{c.plans.openCalc}</Btn>
            </div>
          </div>
        </Section>
      ) : null}

      {/* ── El calculador ── */}
      {mode === 'calc' ? (
        <section style={{ padding: pad, display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: '100%', maxWidth: 1140, display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(0, 1.25fr) minmax(340px, 1fr)', gap: 28, alignItems: 'start' }}>
            <div style={{ display: 'grid', gap: 20, order: narrow ? 2 : 1 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                {PRESETS.map((ps) => (
                  <Btn key={ps.id} onClick={() => applyPreset(ps.id)} hover="xkp-hover-tile" style={{ background: preset === ps.id ? 'rgba(178,98,245,0.06)' : C.white, border: `1.5px solid ${preset === ps.id ? C.accent : C.lilacLine}`, borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, minHeight: 124, transition: `all 150ms ${EASE}` }}>
                    <div style={mono({ fontSize: 30, lineHeight: '34px', letterSpacing: '-0.02em' })}>{ps.s.e}</div>
                    <div style={{ fontSize: 11, color: '#666666' }}>{c.calc.eventsAYear}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.ink, marginTop: 6 }}>{c.calc.presets[ps.id].name}</div>
                    <div style={{ fontSize: 11, color: '#666666', textAlign: 'center' }}>{c.calc.presets[ps.id].sub}</div>
                  </Btn>
                ))}
              </div>

              <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 16, padding: 26, display: 'grid', gap: 28 }}>
                {MAIN_INPUTS.map((m) => (
                  <div key={m.key}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                      <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: C.text }}>{c.calc.inputs[m.key].label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                        <Btn onClick={() => step(m.key, -m.step, m.min, m.max)} hover="xkp-hover-lilac" ariaLabel="−" style={{ width: 38, height: 38, borderRadius: 999, background: C.lilac, color: C.ink, fontFamily: MONO, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `background 150ms ${EASE}` }}>−</Btn>
                        <input
                          type="number"
                          value={calc[m.key]}
                          onChange={(e) => num(m.key, e.target.value, m.min, m.max)}
                          min={m.min} max={m.max} step={m.step}
                          aria-label={c.calc.inputs[m.key].label}
                          style={{ width: 86, padding: '9px 12px', borderRadius: 10, border: `1.5px solid ${C.lilac}`, background: C.white, color: C.ink, fontFamily: MONO, fontSize: 17, fontWeight: 700, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                        />
                        <Btn onClick={() => step(m.key, m.step, m.min, m.max)} hover="xkp-hover-lime" ariaLabel="+" style={{ width: 38, height: 38, borderRadius: 999, background: C.lime, color: C.ink, fontFamily: MONO, fontSize: 20, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `background 150ms ${EASE}` }}>+</Btn>
                      </div>
                    </div>
                    <input type="range" value={calc[m.key]} onChange={(e) => num(m.key, e.target.value, m.min, m.max)} min={m.min} max={m.max} step={m.step} aria-label={c.calc.inputs[m.key].label} style={{ width: '100%', height: 26, marginTop: 10, accentColor: C.accent }} />
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>{c.calc.inputs[m.key].hint}</div>
                  </div>
                ))}
              </div>

              <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 16, padding: '22px 26px', display: 'grid', gap: 14 }}>
                <div style={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: C.text }}>{c.calc.needs.title}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {NEED_IDS.map((n) => {
                    const on = calc.needs[n];
                    return (
                      <Btn key={n} onClick={() => toggleNeed(n)} hover="xkp-hover-chip" style={{ padding: '12px 16px', borderRadius: 999, border: on ? `1.5px solid ${C.accent}` : `1px solid ${C.line}`, background: on ? 'rgba(178,98,245,0.15)' : C.white, color: on ? C.deep : C.dim, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 7, transition: `all 150ms ${EASE}` }}>
                        <span style={{ fontFamily: MONO, fontSize: 13 }}>{on ? '✓' : '+'}</span><span>{c.calc.needs[n]}</span>
                      </Btn>
                    );
                  })}
                </div>
                <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>
                  {activeNeeds.length ? fill(c.calc.needs.hintSome, { list: activeNeeds.join(', ') }) : c.calc.needs.hintNone}
                </div>
              </div>

              <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 16, padding: '22px 26px', display: 'grid', gap: 14 }}>
                <Btn onClick={() => setDetail((d) => !d)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 14, lineHeight: '20px', fontWeight: 600, color: C.text }}>{c.calc.fineTune}</div>
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>{detailSummary}</div>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 18, color: C.accent, flex: 'none' }}>{detail ? '−' : '+'}</div>
                </Btn>
                {detail ? (
                  <div style={{ display: 'grid', gap: 2, borderTop: `1px solid ${C.hair}`, paddingTop: 10 }}>
                    {DETAIL_ROWS.map((d) => (
                      <div key={d.key} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center', padding: '9px 0' }}>
                        <div>
                          <div style={{ fontSize: 14, lineHeight: '20px', color: C.text }}>{c.calc.detailRows[d.key].name}</div>
                          <div style={{ fontSize: 12, lineHeight: '18px', color: C.dim }}>{c.calc.detailRows[d.key].desc}</div>
                        </div>
                        {/* El Counter del kit: −, cifra, + en una píldora. */}
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 0, border: `1.5px solid ${C.lilacLine}`, borderRadius: 999, overflow: 'hidden', flex: 'none' }}>
                          <button type="button" aria-label="−" onClick={() => num(d.key, calc[d.key] - 1, d.min, d.max)} style={{ background: 'transparent', border: 0, padding: '6px 14px', fontSize: 18, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>−</button>
                          <span style={{ minWidth: 44, textAlign: 'center', fontFamily: MONO, fontWeight: 700, fontSize: 16, color: C.ink }}>{calc[d.key]}</span>
                          <button type="button" aria-label="+" onClick={() => num(d.key, calc[d.key] + 1, d.min, d.max)} style={{ background: 'transparent', border: 0, padding: '6px 14px', fontSize: 18, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            {/* ── La respuesta ── */}
            <div style={{ position: narrow ? 'static' : 'sticky', top: 92, order: narrow ? 1 : 2 }}>
              <div style={{ background: C.white, border: `1.5px solid ${C.accent}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 8px rgba(27,15,43,0.08)' }}>
                <div style={{ padding: '26px 26px 22px' }}>
                  <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, color: C.dim }}>{c.calc.answer.yourPlan}</div>
                  <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 30, lineHeight: '36px', letterSpacing: '-0.02em', marginTop: 8, color: C.ink }}>{rec.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 12 }}>
                    <div style={mono({ fontSize: 44, lineHeight: '48px', letterSpacing: '-0.03em' })}>{recTotal}</div>
                    <div style={{ fontSize: 14, color: C.dim }}>{rec.total == null ? '' : c.calc.answer.perYear}</div>
                  </div>
                  <div style={{ fontSize: 15, lineHeight: '22px', fontWeight: 600, color: C.text, marginTop: 8, fontVariantNumeric: 'tabular-nums' }}>
                    {rec.total == null ? c.calc.answer.scoped
                      : rec.total === 0 ? c.calc.answer.freeInside
                        : q.rec === 'enterprise' ? fill(c.calc.answer.fromMonth, { amount: fmt(rec.total / 12) })
                          : q.rec === 'alc' ? fill(c.calc.answer.monthEquiv, { amount: fmt(rec.total / 12) })
                            : fill(c.calc.answer.month, { amount: fmt(rec.total / 12) })}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: '21px', color: C.dim, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {rec.total == null || calc.e === 0 || rec.total === 0 ? '' : fill(q.rec === 'enterprise' ? c.calc.answer.perEventFrom : c.calc.answer.perEvent, { amount: fmt(rec.total / calc.e), n: calc.e })}
                  </div>
                  <Btn onClick={recGo} hover="xkp-hover-purple" style={{ height: 52, borderRadius: 999, background: C.accent, color: C.white, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 18, transition: `background 150ms ${EASE}` }}>{recCta}</Btn>
                </div>

                {bannerText ? (
                  <div style={{ padding: '16px 26px', background: bannerBg, borderTop: `1px solid ${C.hair}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 7, height: 7, borderRadius: 999, background: bannerDot, marginTop: 7, flex: 'none' }} />
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.text }}>{bannerText}</div>
                  </div>
                ) : null}

                <div style={{ padding: '20px 26px', borderTop: `1px solid ${C.hair}`, display: 'grid', gap: 9 }}>
                  <div style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, color: C.dim }}>{c.calc.answer.turnsOn}</div>
                  {unlockRows(c.unlocks[q.rec], false, c.plans.soon).map((u, i) => <Check key={i} size={14}>{u.text}</Check>)}
                  <SealRow text={c.plans.seal} size={13} />
                </div>

                <div style={{ padding: '18px 26px 22px', borderTop: `1px solid ${C.hair}` }}>
                  <Btn onClick={() => setShowLines((v) => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: C.deep, fontWeight: 600 }}>
                    <span>{c.calc.answer.howBuilt}</span>
                    <span style={{ fontFamily: MONO }}>{showLines ? '−' : '+'}</span>
                  </Btn>
                  {showLines ? (
                    <div style={{ display: 'grid', gap: 6, paddingTop: 12 }}>
                      {rec.lines.map((li) => (
                        <div key={li.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, fontSize: 13, lineHeight: '20px', color: C.mid }}>
                          <span>{lineLabel(li.id)}</span>
                          <span style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums', color: li.included ? C.accent : C.ink }}>{li.included ? c.calc.answer.included : fmt(li.amount)}</span>
                        </div>
                      ))}
                      {q.alcShown && q.rec !== 'alc' ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, fontSize: 13, lineHeight: '20px', color: C.dim, borderTop: `1px solid ${C.hair}`, paddingTop: 8, marginTop: 4 }}>
                        <span>{c.calc.answer.alcCompare}</span>
                        <span style={{ fontFamily: MONO, fontVariantNumeric: 'tabular-nums' }}>{fmt(q.alcTotal)}</span>
                      </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
                {neighbours.map((nb) => (
                  <Btn key={nb.title} onClick={nb.pick} hover="xkp-hover-border" style={{ border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 18px', background: C.white, display: 'grid', gap: 5, transition: `border-color 150ms ${EASE}` }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{nb.title}</div>
                      <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: nb.deltaColor }}>{nb.delta}</div>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: '20px', color: C.dim }}>{nb.body}</div>
                  </Btn>
                ))}
              </div>

              <div style={{ fontSize: 12, lineHeight: '19px', color: C.dim, marginTop: 14, padding: '0 4px' }}>{fill(c.calc.answer.grace, { pct: graceOf(T, q.rec) })}</div>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
